import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { ChangeDetectionStrategy, Component, PLATFORM_ID, inject } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { ABOUT_TRANSLATIONS } from '../core/about-translations.generated';
import {
  LANGUAGE_DIRECTIONS,
  SUPPORTED_LANGUAGE_CODES,
} from '../core/supported-language-codes.generated';
import { LocalizationService } from '../core/localization.service';
import { publicSiteUrl } from '../core/public-url';
import { DsButtonDirective, DsCardDirective } from '../ui';

@Component({
  selector: 'app-landing-page',
  imports: [RouterLink, DsButtonDirective, DsCardDirective],
  templateUrl: './landing-page.html',
  styleUrl: './landing-page.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LandingPage {
  private readonly route = inject(ActivatedRoute);
  private readonly i18n = inject(LocalizationService);
  private readonly document = inject(DOCUMENT);
  private readonly title = inject(Title);
  private readonly meta = inject(Meta);
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  protected readonly language =
    (this.route.snapshot.data['language'] as string | undefined) ?? 'en';
  private readonly translations = ABOUT_TRANSLATIONS[this.language] ?? ABOUT_TRANSLATIONS['en'];

  constructor() {
    this.applySeo();
    if (this.isBrowser) {
      void this.i18n.setLanguage(this.language).then(() => this.applySeo());
    }
  }

  protected t(key: string): string {
    return this.translations[key] ?? ABOUT_TRANSLATIONS['en'][key] ?? key;
  }

  private applySeo(): void {
    const canonical = publicSiteUrl(`/${this.language}/about`);
    const pageTitle = `${this.t('about.title')} | IQLabTest`;
    const description = this.t('about.lede');

    this.document.documentElement.lang = this.language;
    this.document.documentElement.dir = LANGUAGE_DIRECTIONS[this.language] ?? 'ltr';
    this.title.setTitle(pageTitle);
    this.meta.updateTag({ name: 'description', content: description });
    this.meta.updateTag({ name: 'robots', content: 'index, follow, max-image-preview:large' });
    this.meta.updateTag({ property: 'og:type', content: 'website' });
    this.meta.updateTag({ property: 'og:title', content: pageTitle });
    this.meta.updateTag({ property: 'og:description', content: description });
    this.meta.updateTag({ property: 'og:url', content: canonical });
    this.meta.updateTag({ property: 'og:site_name', content: 'IQLabTest' });

    let canonicalLink = this.document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = this.document.createElement('link');
      canonicalLink.rel = 'canonical';
      this.document.head.appendChild(canonicalLink);
    }
    canonicalLink.href = canonical;

    this.document.head
      .querySelectorAll('link[data-iqlabtest-alternate]')
      .forEach((element) => element.remove());
    for (const language of SUPPORTED_LANGUAGE_CODES) {
      const alternate = this.document.createElement('link');
      alternate.rel = 'alternate';
      alternate.hreflang = language;
      alternate.href = publicSiteUrl(`/${language}/about`);
      alternate.setAttribute('data-iqlabtest-alternate', 'true');
      this.document.head.appendChild(alternate);
    }
    const fallback = this.document.createElement('link');
    fallback.rel = 'alternate';
    fallback.hreflang = 'x-default';
    fallback.href = publicSiteUrl('/en/about');
    fallback.setAttribute('data-iqlabtest-alternate', 'true');
    this.document.head.appendChild(fallback);

    this.document.getElementById('about-structured-data')?.remove();
    const structuredData = this.document.createElement('script');
    structuredData.id = 'about-structured-data';
    structuredData.type = 'application/ld+json';
    structuredData.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'AboutPage',
      name: this.t('about.title'),
      description,
      inLanguage: this.language,
      url: canonical,
      isPartOf: { '@type': 'WebSite', name: 'IQLabTest', url: publicSiteUrl() },
    });
    this.document.head.appendChild(structuredData);
  }
}
